import { Button, Result, Space } from "antd";
import { useEffect, useMemo } from "react";
import { useRouteError } from "react-router-dom";
import { frontendEnvironment } from "../api/env";
import {
  attemptModuleLoadRecovery,
  classifyModuleLoadFailure,
} from "./moduleLoadRecovery";

function safeCorrelationId(error: unknown): string | undefined {
  if (typeof error !== "object" || error === null) return undefined;
  const value = (error as { correlationId?: unknown }).correlationId;
  if (typeof value !== "string" || !/^[A-Za-z0-9._-]{1,80}$/.test(value)) {
    return undefined;
  }
  return value;
}

function localErrorCode(): string {
  const build = frontendEnvironment.buildCommit.slice(0, 8) || "unknown";
  return `MBIP-${build}-${Date.now().toString(36).toUpperCase()}`;
}

function reloadMbip(): void {
  if (typeof window !== "undefined") window.location.reload();
}

function returnHome(): void {
  if (typeof window !== "undefined") window.location.assign("/");
}

export function MbipRouteErrorView({ error }: { error: unknown }) {
  const moduleFailure = useMemo(() => classifyModuleLoadFailure(error), [error]);
  const errorCode = useMemo(
    () => safeCorrelationId(error) ?? localErrorCode(),
    [error],
  );

  useEffect(() => {
    if (moduleFailure === undefined) return;
    console.error("MBIP_MODULE_LOAD_FAILURE", {
      category: moduleFailure.category,
      requestedChunkUrl: moduleFailure.requestedUrl,
      frontendBuildCommit: frontendEnvironment.buildCommit,
      errorCode,
    });
    attemptModuleLoadRecovery(error);
  }, [error, errorCode, moduleFailure]);

  if (moduleFailure !== undefined) {
    return (
      <main className="page-stack" role="alert">
        <Result
          status="error"
          title="MBIP couldn't load this application module."
          subTitle="The application may have been updated. Reload MBIP to continue."
          extra={
            <Space wrap>
              <Button type="primary" onClick={reloadMbip}>Reload MBIP</Button>
              <Button onClick={returnHome}>Return to Home</Button>
            </Space>
          }
        />
        <p className="technical-detail">Error code: <code>{errorCode}</code></p>
      </main>
    );
  }

  return (
    <main className="page-stack" role="alert">
      <Result
        status="error"
        title="MBIP couldn't complete this request."
        subTitle="Try returning home. If the problem continues, share the error code with support."
        extra={<Button type="primary" onClick={returnHome}>Return to Home</Button>}
      />
      <p className="technical-detail">Error code: <code>{errorCode}</code></p>
    </main>
  );
}

export function MbipRouteErrorBoundary() {
  return <MbipRouteErrorView error={useRouteError()} />;
}
